import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Route } from 'lucide-react';
import { joinTrack, listenToTracks, useCoverage, useMyTrackIds, type Track } from '@core/firebase/tracks';
import { trackProgress } from '@core/quran/trackProgress';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ProgressBar } from '@/components/ProgressBar';
import { LearningEmpty, PageHeader, PILL_LIME, PILL_PRIMARY } from '@/components/learning/ui';
import { useLearnText } from '@/lib/learnText';
import { cn } from '@/lib/cn';

/**
 * The tracks a student can take, with how far through each one they are.
 *
 * Progress is worked out here from the student's own coverage rather than read
 * from their standing: the standing exists for the BOARD, and waiting on one
 * per row would be a round trip per track just to draw a bar.
 *
 * Redesign 2026-10 (board l7), at desktop width: a grid of soft cards, each
 * with its length, a lime «مشترك» pill and progress once joined, and one
 * labelled action — «تابع» into the track, or «ابدأ المسار» to join it.
 */
export default function TracksPage() {
  const { identity } = useAuth();
  const { t } = useLanguage();
  const tl = useLearnText();
  const [tracks, setTracks] = useState<Track[]>([]);
  const { trackIds } = useMyTrackIds(identity?.userId ?? null);
  const coverage = useCoverage(identity?.userId ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => listenToTracks(setTracks), []);

  const toggle = async (track: Track, joined: boolean) => {
    if (busy) return;
    setBusy(track.id);
    setErrorKey(null);
    try {
      await joinTrack(track.id, joined);
    } catch (e) {
      // The server refuses a student with no name, because the board shows
      // names. Say which of the two it was.
      const message = String((e as { message?: string })?.message ?? '');
      setErrorKey(message.includes('no-name') ? 'tracks.needName' : 'tracks.joinFailed');
    } finally {
      setBusy(null);
    }
  };

  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <PageHeader title={t('tracks.title')} subtitle={tl('learn.tracks.subtitle')} />

      {errorKey && (
        <p data-testid="tracks-error" role="alert" className="rounded-[14px] bg-danger/10 p-3 text-sm text-danger">
          {t(errorKey)}
        </p>
      )}

      {tracks.length === 0 ? (
        <LearningEmpty
          testId="no-tracks"
          icon={Route}
          title={t('tracks.emptyTitle')}
          body={t('tracks.emptyBody')}
        />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tracks.map((track) => {
            const joined = trackIds.includes(track.id);
            const progress = trackProgress(coverage, track.surahs);
            return (
              <li
                key={track.id}
                data-testid={`track-${track.id}`}
                className="card-soft flex flex-col gap-4 p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/tracks/${track.id}`}
                      data-testid={`open-${track.id}`}
                      className="text-lg font-semibold text-ink hover:text-primary"
                    >
                      {track.name}
                    </Link>
                    <p className="text-sm text-helper">
                      {t('tracks.surahCount', { count: track.surahs.length, n: count(track.surahs.length) })}
                      {' · '}
                      {t('tracks.ayahCount', { count: track.totalAyahs, n: count(track.totalAyahs) })}
                    </p>
                  </div>
                  {joined ? (
                    <span className="shrink-0 rounded-full bg-lime px-3 py-1 text-xs font-semibold text-accent-foreground">
                      {tl('learn.tracks.joined')}
                    </span>
                  ) : null}
                </div>

                {joined && (
                  <div data-testid={`progress-${track.id}`} className="space-y-1.5">
                    <ProgressBar percent={progress.percent} label={t('tracks.yourProgress')} />
                    <p className="text-xs text-helper">
                      <span dir="ltr">{count(progress.covered)} / {count(progress.total)} · {count(progress.percent)}%</span>
                    </p>
                  </div>
                )}

                <div className="mt-auto flex items-center gap-3">
                  {joined ? (
                    <>
                      <Link
                        to={`/tracks/${track.id}`}
                        data-testid={`continue-${track.id}`}
                        className={cn(PILL_PRIMARY, 'flex-1')}
                      >
                        {tl('learn.tracks.continue')}
                      </Link>
                      <button
                        type="button"
                        data-testid={`leave-${track.id}`}
                        onClick={() => { void toggle(track, joined); }}
                        disabled={busy === track.id}
                        className="shrink-0 rounded-full px-3 py-2 text-sm text-helper hover:bg-surface disabled:opacity-60"
                      >
                        {t('tracks.leave')}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      data-testid={`join-${track.id}`}
                      onClick={() => { void toggle(track, joined); }}
                      disabled={busy === track.id}
                      className={cn(PILL_LIME, 'w-full')}
                    >
                      {tl('learn.tracks.start')}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
